import {
  Authenticated,
  AuthenticationRequested,
  ConsentGranted,
  ConsentRequested,
  Failed,
  IdentificationFlow,
  Initialized,
} from "./domain/IdentificationState";
import { usePoller } from "@cleverbase-trust-drivers/shared/Poller";

import AuthenticationComponent, {
  AuthenticationTextFields,
} from "@cleverbase-trust-drivers/shared/components/authentication/AuthenticationComponent";
import { AuthenticationState } from "@cleverbase-trust-drivers/shared/components/authentication/domain/AuthenticationState";
import ConsentComponent, {
  ConsentTextFields,
} from "@cleverbase-trust-drivers/shared/components/consent/ConsentComponent";
import { useEffect, useState } from "react";
import Error, {
  ErrorText,
} from "@cleverbase-trust-drivers/shared/components/error";
import { RedirectText } from "@cleverbase-trust-drivers/shared/components/RedirectComponent";
import { TFunction } from "i18next";
import Loader from "@cleverbase-trust-drivers/shared/components/base/Loader";
import moveLogo from "./images/move_nl_rgb.png";
import pnLogo from "./images/postnl_logo.png";
import cleverbaseAppIcon from "@cleverbase-trust-drivers/shared/images/cleverbase-app-icon.svg";
import * as Sentry from "@sentry/react";
import {
  addProcessId,
  addStateBreadCrumb,
} from "@cleverbase-trust-drivers/shared/sentry/Events";
import { UseCase } from "@cleverbase-trust-drivers/shared/components/base/UseCase";

export function handleAuthentication(
  initialized: Initialized,
  authenticationInfo: Initialized | AuthenticationRequested | Authenticated,
  isDesktop: boolean,
  useCase: UseCase,
  t: AuthenticationTextFields,
): JSX.Element {
  const makeComponent = (state: AuthenticationState) => (
    <AuthenticationComponent
      relyingPartyName={initialized.relyingPartyName}
      state={state}
      title={initialized.title}
      logoUri={initialized.logoUri}
      contextText={initialized.contextText}
      authenticationText={t}
      isDesktop={isDesktop}
      identityAttributes={initialized.identityAttributes}
      clientContextText={initialized.contextText}
      noAuthenticationSpinner={false}
      useCase={useCase}
    />
  );

  switch (authenticationInfo.type) {
    case "Initialized":
      return makeComponent({ type: "ChallengesRequested" });
    case "AuthenticationRequested":
      console.log("pid: " + initialized.processId);
      addProcessId(initialized.processId);
      return makeComponent({
        type: "ShowChallenges",
        ckChallenge: authenticationInfo.ckChallenge,
      });
    case "Authenticated":
      return makeComponent({
        type: "Authenticated",
        showNonPKIoAlert: authenticationInfo.showNonPkioAlert,
      });
  }
}

export function handleConsent(
  initialized: Initialized,
  consentInfo: ConsentRequested | ConsentGranted,
  isDesktop: boolean,
  useCase: UseCase,
  t: ConsentTextFields,
  showNonPKIoAlert: boolean | undefined,
): JSX.Element {
  return (
    <ConsentComponent
      relyingPartyName={initialized.relyingPartyName}
      attributes={initialized.identityAttributes}
      state={
        consentInfo.type === "ConsentRequested"
          ? { type: "ConsentRequested" }
          : { type: "ConsentGranted", redirectUri: consentInfo.redirectUri }
      }
      isDesktop={isDesktop}
      consentTextFields={t}
      clientContextText={initialized.contextText}
      title={initialized.title}
      logoUri={initialized.logoUri}
      showNonPKIoWarning={!!showNonPKIoAlert ? showNonPKIoAlert : false}
      useCase={useCase}
    />
  );
}

function translateErrorText(
  failedState: Failed,
  redirectText: RedirectText,
  t: TFunction<["identification", "base"]>,
): ErrorText {
  switch (failedState.failure.type) {
    case "AttributeRequestRejected":
      return {
        errorTitle: t("errors.AttributeRequestRejected.title"),
        errorText: t("errors.AttributeRequestRejected.description"),
        redirectText: redirectText,
      };
    case "ChallengeExpired":
      return {
        errorTitle: t("base:errors.ChallengeExpired.title"),
        errorText: t("base:errors.ChallengeExpired.description"),
        redirectText: redirectText,
      };
    case "ConcurrentTransaction":
      return {
        errorTitle: t("base:errors.ConcurrentTransaction.title"),
        errorText: t("base:errors.ConcurrentTransaction.description"),
        redirectText: redirectText,
      };
    case "ConsentExpired":
      return {
        errorTitle: t("base:errors.ConsentExpired.title"),
        errorText: t("base:errors.ConsentExpired.description"),
        redirectText: redirectText,
      };
    case "EmailUnverified":
      return {
        errorTitle: t("base:errors.EmailUnverified.title"),
        errorText: t("base:errors.EmailUnverified.description"),
        redirectText: redirectText,
      };
    case "ConsentRejected":
      return {
        errorTitle: t("identification:errors.ConsentRejected.title"),
        errorText: t("identification:errors.ConsentRejected.description"),
        redirectText: redirectText,
      };
    case "ProcessExpired":
      return {
        errorTitle: t("base:errors.ProcessExpired.title"),
        errorText: t("base:errors.ProcessExpired.description"),
        redirectText: redirectText,
      };
    case "CleverbaseIdBlocked":
      return {
        errorTitle: t("base:errors.CleverbaseIdBlocked.title"),
        errorText: t("base:errors.CleverbaseIdBlocked.description"),
        redirectText: redirectText,
      };
    case "Internal":
    default:
      Sentry.captureException("Internal IDF Error");
      return {
        errorTitle: t("base:errors.Internal.title"),
        errorText: t("base:errors.Internal.description"),
        redirectText: redirectText,
      };
  }
}

function translateAttribute(
  atrrName: string,
  t: TFunction<["identification", "base"]>,
): string {
  switch (atrrName) {
    case "E-mail adres":
      return t("identification:attributes.E-mail adres");
    case "Achternaam":
      return t("identification:attributes.Achternaam");
    case "Voornamen":
      return t("identification:attributes.Voornamen");
    case "Geboortedatum":
      return t("identification:attributes.Geboortedatum");
    case "Geboorteplaats":
      return t("identification:attributes.Geboorteplaats");
    case "Nationaliteit":
      return t("identification:attributes.Nationaliteit");
    case "ID Documentnummer":
      return t("identification:attributes.ID Documentnummer");
    case "Authenticatie certificaat":
      return t("identification:attributes.Authenticatie certificaat");
    case "Ondertekeningscertificaat":
      return t("identification:attributes.Ondertekeningscertificaat");
    case "Voornaam (BRP)":
      return t("identification:attributes.Voornaam (BRP)");
    case "Voorvoegsel (BRP)":
      return t("identification:attributes.Voorvoegsel (BRP)");
    case "Geslachtsnaam (BRP)":
      return t("identification:attributes.Geslachtsnaam (BRP)");
    case "Geslachtsnaam zonder voorvoegsel (BRP)":
      return t(
        "identification:attributes.Geslachtsnaam zonder voorvoegsel (BRP)",
      );
    case "com.cleverbase.id_document_issuance_date":
      return t(
        "identification:attributes.com.cleverbase.id_document_issuance_date",
      );
    case "com.cleverbase.id_document_issuance_place":
      return t(
        "identification:attributes.com.cleverbase.id_document_issuance_place",
      );
    case "com.cleverbase.id_document_expiration_date":
      return t(
        "identification:attributes.com.cleverbase.id_document_expiration_date",
      );
    default:
      return "";
  }
}

function translateTitle(
  title: string,
  t: TFunction<["identification", "base"]>,
): string {
  if (!!title) {
    switch (title) {
      case "Inloggen bij":
        return t("identification:title.login");
      case "Identificeren bij":
        return t("identification:title.identify");
      default:
        return t("identification:title.identify");
    }
  } else return t("identification:title.identify");
}

function translateClientMessage(
  message: string | undefined,
  t: TFunction<["identification", "base"]>,
): string | undefined {
  if (!!message) {
    switch (message) {
      case "Je makelaar wilt dat je de Wwft-check van Cleverbase doet. Log in en deel de info hieronder om te starten":
        return t("identification:consentContext.vw");
      case "Je makelaar wilt dat je jezelf bekend maakt. Deel je ID-gegevens om de actie af te maken.":
        return t("identification:consentContext.move");
      default:
        return message;
    }
  } else return undefined;
}

function imageToLocal(clientName: string | undefined): string | undefined {
  switch (clientName) {
    case "Move.nl":
      return moveLogo;
    case "PostNL":
      return pnLogo;
    case "Cleverbase":
    case "Cleverbase Signing":
      // The app icon, not the wordmark: the tile is square.
      return cleverbaseAppIcon;
    default:
      return undefined;
  }
}

export function handleIdentificationState(
  state: IdentificationFlow | null,
  onEndState: () => void,
  isDesktop: boolean,
  useCase: UseCase,
  translations: {
    authenticationText: AuthenticationTextFields;
    consentText: ConsentTextFields;
    redirectText: RedirectText;
  },
  t: TFunction<["identification", "base"]>,
): JSX.Element {
  if (state?.failed)
    return (
      <Error
        isDesktop={isDesktop}
        redirectUri={state.failed.maybeRedirectUri}
        t={translateErrorText(state.failed, translations.redirectText, t)}
        clientName={state.initialized?.relyingPartyName}
        processId={state.initialized?.processId}
        errorVariant="identification"
      />
    );

  if (!state?.initialized) return <Loader />;

  if (state.consentGranted || state.failed) onEndState();

  const maybeConsentState = state?.consentGranted ?? state?.consentRequested;

  const maybeAuthState =
    state?.authenticated ??
    state?.authenticationRequested ??
    state?.initialized;

  const translatedIntialized: Initialized = {
    ...state.initialized,
    identityAttributes: state.initialized.identityAttributes.map((attributes) =>
      translateAttribute(attributes, t),
    ),
    contextText: translateClientMessage(state.initialized.contextText, t),
    title: translateTitle(state.initialized.title, t),
    logoUri: imageToLocal(state.initialized.relyingPartyName),
  };

  let coreElement: JSX.Element = maybeConsentState
    ? handleConsent(
        translatedIntialized,
        maybeConsentState,
        isDesktop,
        useCase,
        translations.consentText,
        state.authenticated?.showNonPkioAlert,
      )
    : handleAuthentication(
        translatedIntialized,
        maybeAuthState,
        isDesktop,
        useCase,
        translations.authenticationText,
      );

  return coreElement;
}

export default function IdentificationComponent({
  client,
  rateMs,
  isDesktop,
  translations,
  t,
}: {
  client: () => Promise<IdentificationFlow>;
  rateMs: number;
  isDesktop: boolean;
  translations: {
    authenticationText: AuthenticationTextFields;
    consentText: ConsentTextFields;
    redirectText: RedirectText;
  };
  t: TFunction<["identification", "base"]>;
}) {
  const [state, setState] = useState<IdentificationFlow | null>(null);
  const { stopPolling } = usePoller(client, setState, rateMs);

  useEffect(() => {
    addStateBreadCrumb(
      "identification-component",
      state?.failed?.type ??
        state?.consentGranted?.type ??
        state?.consentRequested?.type ??
        state?.authenticated?.type ??
        state?.authenticationRequested?.type ??
        state?.initialized?.type,
    );
  }, [state]);

  const attributes = state?.initialized?.identityAttributes;
  const useCase =
    attributes && attributes.length > 0 ? "Identification" : "EasyLogin";

  const coreComponent = handleIdentificationState(
    state,
    stopPolling,
    isDesktop,
    useCase,
    translations,
    t,
  );

  return coreComponent;
}
